import { t } from "@lingui/macro"
import { Alert, Button } from "@mui/material"
import { observer } from "mobx-react"
import React, { useCallback, useMemo } from "react"

import { AlertSettingsSection } from "./sections/Settings"
import { AlertContentSection } from "./sections/Content"
import { AlertDetailModalStore } from "./store"

import { Form } from "src/components/Form"
import { ModalHeader } from "src/components/ModalHeader"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { useCloseConfirmationForForm } from "src/store/modals/use-close-confirmation-for-form"
import { useInitializer } from "src/lib/initializer"
import { CenteredSpinner } from "src/components/CenteredSpinner"
import { FormPanel } from "src/components/FormPanel"
import { EModalMode } from "src/types/modal/modal"
import { GlobalStore } from "src/store"
import { IAlertDetailModalProps } from "src/modals/alert-detail/types"

const Modal = observer(({ id, mode }: IAlertDetailModalProps) => {
    const store = useStore(AlertDetailModalStore)
    const gstore = useStore(GlobalStore)
    useCloseConfirmationForForm(store.formFields)

    // Map string mode to EModalMode
    let enumMode: EModalMode | undefined = undefined
    if (mode === "Edit") enumMode = EModalMode.Edit
    else if (mode === "View") enumMode = EModalMode.View
    else if (mode === "Copy") enumMode = EModalMode.Copy

    const initialized = useInitializer(async () => {
        await store.init(
            id ?? null,
            gstore.session.accessGroup.id,
            enumMode,
            gstore.session.accessGroup.name,
        )
    }, [
        id,
        enumMode,
        gstore.session.accessGroup.id,
        gstore.session.accessGroup.name,
    ])

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            const success = await store.handleSubmit()
            if (success) {
                gstore.modals.pop()
            }
        },
        [store, gstore.modals],
    )

    const isDisabled = useMemo(() => {
        return (
            store.isLoading ||
            (!store.formFields.getIsDirty() && !store.isSegmentsDirty)
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.isLoading, store.isSegmentsDirty, store.formFields.getIsDirty()])

    if (!initialized) return <CenteredSpinner height="45vh" />

    return (
        <Form onSubmit={handleSubmit}>
            <ModalHeader>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isDisabled}
                    data-testid="submit-alert-form"
                >
                    {t`global.save`}
                </Button>
            </ModalHeader>
            {Boolean(store.formFields.error("generic")) && (
                <Alert severity="error">
                    {store.formFields.error("generic")}
                </Alert>
            )}
            <FormPanel
                sections={[
                    {
                        header: t`alerts-modal.settings-section`,
                        content: <AlertSettingsSection />,
                    },
                ]}
            />
            <FormPanel
                sections={[
                    {
                        header: t`alerts-modal.content-section`,
                        content: <AlertContentSection />,
                    },
                ]}
            />
        </Form>
    )
})

export const AlertDetailModal = (props: IAlertDetailModalProps) => (
    <StoreProvider Store={AlertDetailModalStore}>
        <Modal {...props} />
    </StoreProvider>
)
